import { type JSX } from 'react';

import { useNothingSelectedDismissed } from '@/features/sharepoint/nothing-selected-dismissal.js';
import {
  ownerReachesNothing,
  reachName,
  useSelectedFolderCount,
} from '@/features/sharepoint/selected-folders.js';
import { cn } from '@/lib/utils.js';

/**
 * The way into a connection's folder list, stating how much of the connection is exposed here.
 *
 * The count is the point. A SharePoint connection reaches nothing until somebody picks what it may
 * reach, so "connected" on its own says almost nothing — and a connection sitting at zero looks
 * exactly like a working one until this number is on the card.
 *
 * A count that could not be read is not shown. Either half of it failing would understate the total,
 * and an understated total reads as "less is exposed than really is" — the wrong direction for a
 * number somebody uses to decide whether to go and configure something.
 */
export function SelectFoldersButton({
  workspaceId,
  connectionId,
  onOpen,
  className,
}: {
  workspaceId: string;
  connectionId: string;
  onOpen: () => void;
  className?: string;
}): JSX.Element {
  const count = useSelectedFolderCount(workspaceId, connectionId);
  const isDismissed = useNothingSelectedDismissed();
  /**
   * Marked only for whoever can clear it. Only a connection's owner may change its list, so urging
   * anyone else costs them a red dot they can never act on — and a mark nobody can clear is one
   * everybody learns to ignore. Clearing it also means closing the list here with nothing on it:
   * a travelling connection is meant to reach nothing in most workspaces.
   */
  const nothingSelected =
    ownerReachesNothing(count) && !isDismissed(workspaceId, connectionId);
  /**
   * A connection that is not restricted reaches the whole SharePoint account, so that is what the
   * card says. Its count would describe what has been collected for later, and next to a connection
   * that works it read as "exposes nothing". OneDrive is named only when the whole drive is reached.
   */
  const label = !count.ready
    ? 'Selected'
    : count.restricted
      ? `Selected ${count.selected} ${count.selected === 1 ? 'item' : 'items'}`
      : `All of ${reachName(count.wholeOneDrive)}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-3 py-1 text-xs font-medium transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20',
        className,
      )}
    >
      {nothingSelected && (
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full bg-red-500"
        />
      )}
      {/*
        Not "folders", and no denominator.

        SharePoint takes a site, a document library, a folder or a single file, and OneDrive a
        folder or a single file. And the denominator was two ceilings added together — 25 places plus
        10 folders — which is a number nobody can reach on either list. What the card is for is the
        count: a connection exposing nothing looks exactly like a working one without it.
      */}
      <span>{label}</span>
    </button>
  );
}
